import React, { useState, useEffect, useMemo } from 'react'

import {
  Icon,
  Button,
  Block,
  Tooltip,
} from '@cgtrader/triangle-ui/dist/src/ssr-index'
import {
  string,
  number,
  oneOfType,
  object,
  bool,
  oneOf,
  shape,
  arrayOf,
} from 'prop-types'

import { usePreviewMode } from 'Pods/ItemPage/context/PreviewModeContext'
import { useIs3dPrintModel } from 'Pods/ItemPage/TopSection/hooks/useTopSectionProps'
import { MULTIPLE_PARTS_UNIT_ABBREVIATIONS } from 'Pods/Profile/Products/ModelUploader/Components/constants'
import usePageConfig from 'Pods/Shared/Hooks/usePageConfig'
import SmartTooltip from 'Pods/Shared/SmartTooltip/SmartTooltip'

import { EMPTY_OBJECT } from 'Utils/stableDefaults'

import {
  getUnitLabel,
  getGeometryTypeTooltip,
  getVisualAttributeTooltip,
  PROVIDED_BY_DESIGNER_TOOLTIP,
  POLYGONS_TOOLTIP,
  VERTICES_TOOLTIP,
} from './Utils'
import DetailsAreaSmallTag from '../DetailsAreaSmallTag/DetailsAreaSmallTag'

const selectIsCurrentUser = (config) => Boolean(config?.isCurrentUser)

let ReportModal
let AssemblyInstructionsModal

function ProvidedByDesignerSection({
  publishDate = null,
  id,
  details = null,
  polygons = null,
  vertices = null,
  triangles = null,
  geometryType = null,
  units,
  isCollection = false,
  unwrappedUvs = null,
  itemsPerRow = 4,
  isPreSupported = false,
  isSplitIntoMultipleParts = false,
  multiplePartsDetails = EMPTY_OBJECT,
  tagForLinking,
}) {
  const is3dPrintModel = useIs3dPrintModel()
  const { isPreview } = usePreviewMode()
  const [reportModalIsOpen, setReportModalIsOpen] = useState(false)
  const [isReportModalLoaded, setIsReportModalLoaded] = useState(false)
  const [assemblyInstructionsModalIsOpen, setAssemblyInstructionsModalIsOpen] =
    useState(false)
  const [
    isAssemblyInstructionsModalLoaded,
    setIsAssemblyInstructionsModalLoaded,
  ] = useState(false)
  const isLoggedIn = usePageConfig({ select: selectIsCurrentUser })

  const geometryTypeTooltip = getGeometryTypeTooltip(geometryType)

  useEffect(() => {
    if (reportModalIsOpen && !ReportModal && !isReportModalLoaded) {
      importReportModal()
    }
  }, [reportModalIsOpen, isReportModalLoaded])

  function importReportModal() {
    import('Pods/LazyLoaded/ReportModal/ReportModal')
      .then((module) => {
        ReportModal = module.default
        setIsReportModalLoaded(true)
      })
      .catch((error) => {
        console.error('Failed to load ReportModal:', error)
      })
  }

  useEffect(() => {
    if (
      assemblyInstructionsModalIsOpen &&
      !AssemblyInstructionsModal &&
      !isAssemblyInstructionsModalLoaded
    ) {
      importAssemblyInstructionsModal()
    }
  }, [assemblyInstructionsModalIsOpen, isAssemblyInstructionsModalLoaded])

  function importAssemblyInstructionsModal() {
    import(
      'Pods/LazyLoaded/MultiplePartsAssemblyModal/MultiplePartsAssemblyModal'
    )
      .then((module) => {
        AssemblyInstructionsModal = module.default
        setIsAssemblyInstructionsModalLoaded(true)
      })
      .catch((error) => {
        console.error('Failed to load MultiplePartsAssemblyModal:', error)
      })
  }

  function handleReportButtonMouseEnter() {
    if (!isReportModalLoaded && !ReportModal) {
      importReportModal()
    }
  }

  function handleReportButtonTouchStart() {
    if (!isReportModalLoaded && !ReportModal) {
      importReportModal()
    }
  }

  function handleAssemblyInstructionsButtonMouseEnter() {
    if (!isAssemblyInstructionsModalLoaded && !AssemblyInstructionsModal) {
      importAssemblyInstructionsModal()
    }
  }

  function handleAssemblyInstructionsButtonTouchStart() {
    if (!isAssemblyInstructionsModalLoaded && !AssemblyInstructionsModal) {
      importAssemblyInstructionsModal()
    }
  }

  const handleReportClick = () => {
    if (!isLoggedIn) {
      window.showNotification('Please log in to report this content', {
        type: 'error',
      })
      return
    }
    setReportModalIsOpen(true)
  }

  const handleCloseReportModal = () => {
    setReportModalIsOpen(false)
  }

  const shouldShowGeometrySection =
    !is3dPrintModel &&
    (Boolean(polygons) ||
      Boolean(vertices) ||
      Boolean(triangles) ||
      typeof geometryType === 'string')

  const shouldDisplayVisualAttributes = !is3dPrintModel || isCollection

  const formatUnwrappedUvValue = (value) => {
    if (!value) return 'Unknown'

    const valueMap = {
      'yes': 'Yes',
      'no': 'No',
      'unknown': 'Unknown',
      'mixed': 'Mixed',
      'non-overlapping': 'Non-overlapping',
      'non_overlapping': 'Non-overlapping',
      'overlapping': 'Overlapping',
    }
    return valueMap[value] || 'Unknown'
  }

  const geometryItems = useMemo(() => {
    const items = []

    if (polygons) {
      items.push({
        key: 'polygons',
        content: `${polygons} polygons`,
        tooltip: POLYGONS_TOOLTIP,
      })
    }

    if (triangles) {
      items.push({
        key: 'triangles',
        content: `${triangles} triangles`,
        tooltip: null,
      })
    }

    if (vertices) {
      items.push({
        key: 'vertices',
        content: `${vertices} vertices`,
        tooltip: VERTICES_TOOLTIP,
      })
    }

    return items
  }, [polygons, triangles, vertices])

  const features = useMemo(() => {
    if (!details || isCollection || Array.isArray(details)) return []

    return [
      {
        title: 'Animated',
        value: details.animated ?? false,
        key: 'animated',
        tag: tagForLinking,
      },
      {
        title: 'Rigged',
        value: details.rigged ?? false,
        key: 'rigged',
        tag: tagForLinking,
      },
      {
        title: 'Low-poly',
        value: details.gameReady ?? false,
        key: 'game_ready',
        tag: tagForLinking,
      },
      { title: 'PBR', value: details.pbr ?? false, key: 'pbr' },
      {
        title: 'Simulation-ready',
        value: details.isSimulationReady ?? false,
        key: 'simulation_ready',
      },
      { title: 'Textures', value: details.textures ?? false, key: 'textures' },
      {
        title: 'Materials',
        value: details.materials ?? false,
        key: 'materials',
      },
      {
        title: 'UV Mapping',
        value: details.uvMapping ?? false,
        key: 'uv_mapping',
      },
      {
        title: 'Plugins Used',
        value: details.pluginsUsed ?? false,
        key: 'plugins_used',
      },
    ].filter(feature => feature.value)
  }, [details, isCollection, tagForLinking])

  const visualAttributesData = useMemo(() => {
    if (!details) return []

    if (isCollection && Array.isArray(details)) {
      return details.map(({ title, value }, index) => {
        const [numerator, denominator] = value
          ?.toString()
          .split('/')
          .map((num) => parseInt(num, 10)) || [0, 0]

        return {
          key: `${title}-${index}`,
          title,
          active: numerator > 0 && numerator === denominator,
          quantity: value,
        }
      })
    }

    return features.map(({ title, value, key, tag = undefined }) => ({
      key,
      title,
      active: value,
      tooltip: getVisualAttributeTooltip(title),
      autoPosition: true,
      containerSelector: '.details-area__section-content',
      tag,
    }))
  }, [details, isCollection, features])

  const visualAttributesRows = useMemo(() => {
    const rows = []
    for (let i = 0; i < visualAttributesData.length; i += itemsPerRow) {
      rows.push(visualAttributesData.slice(i, i + itemsPerRow))
    }
    return rows
  }, [visualAttributesData, itemsPerRow])

  const { numberOfParts, unit, width, depth, height, assemblyInstructions } =
    multiplePartsDetails ?? {}

  const multiplePartsSummary = useMemo(() => {
    if (!isSplitIntoMultipleParts || !is3dPrintModel) {
      return { dimensions: null, instructions: null }
    }

    const dimensionUnitLabel =
      MULTIPLE_PARTS_UNIT_ABBREVIATIONS[unit] ||
      MULTIPLE_PARTS_UNIT_ABBREVIATIONS[units] ||
      unit ||
      units ||
      ''

    const formatDimension = (label, value) => {
      if (!value) return null
      const numericValue = Number(value)
      if (!Number.isFinite(numericValue) || numericValue <= 0) {
        return null
      }

      const formattedValue = numericValue.toString()

      return `${label}: ${formattedValue}${
        dimensionUnitLabel ? ` ${dimensionUnitLabel}` : ''
      }`
    }

    const formattedDepth = formatDimension('Depth', depth)
    const formattedWidth = formatDimension('Width', width)
    const formattedHeight = formatDimension('Height', height)

    const dimensions = [
      formattedDepth ? formattedDepth.replace('Depth: ', '') : null,
      formattedWidth ? formattedWidth.replace('Width: ', '') : null,
      formattedHeight ? formattedHeight.replace('Height: ', '') : null,
    ].filter(Boolean)

    const dimensionValue = dimensions.length > 0 ? dimensions.join(' x ') : null

    const trimmedInstructions = assemblyInstructions?.trim()

    return {
      dimensions: dimensionValue,
      instructions: trimmedInstructions || null,
    }
  }, [
    isSplitIntoMultipleParts,
    unit,
    width,
    depth,
    height,
    assemblyInstructions,
    units,
  ])

  const splitIntoPartsLabel = useMemo(() => {
    if (!isSplitIntoMultipleParts || !is3dPrintModel) {
      return 'No'
    }

    if (!numberOfParts || numberOfParts < 1) {
      return 'Yes'
    }

    return numberOfParts
  }, [isSplitIntoMultipleParts, numberOfParts])

  return (
    <>
      <div className="details-area__section details-area__section--provided-by-designer">
        <div className="details-area__section-header">
          <Tooltip
            content={PROVIDED_BY_DESIGNER_TOOLTIP}
            id="provided-by-designer"
            delayHide={100}
            place="right"
            tooltipClassName="details-area__tooltip-content"
          >
            <span className="details-area__section-title">
              Provided by designer
            </span>
          </Tooltip>
        </div>
        <div className="details-area__section-content">
          {shouldDisplayVisualAttributes && visualAttributesRows.length > 0 && (
            <div className="details-area__section">
              <span className="details-area__section-subtitle">
                3D Features
              </span>
              <div className="details-area__visual-attributes">
                {visualAttributesRows.map((row, rowIndex) => (
                  <div
                    key={`row-${rowIndex}`}
                    className="details-area__visual-attributes-row"
                  >
                    {row.map(({ key, ...rest }) => (
                      <DetailsAreaSmallTag key={key} {...rest} />
                    ))}
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="details-area__section">
            <SmartTooltip
              place="right"
              id="3d-printing"
              delayHide={100}
              delayUpdate={100}
              content={
                `Indicates whether the designer marked ${
                  isCollection ? 'models' : 'this model'
                } ` + 'as suitable for 3D printing.'
              }
            >
              <span className="details-area__section-subtitle">
                3D printing
              </span>
            </SmartTooltip>
            <div className="details-area__visual-attributes">
              {is3dPrintModel ? (
                <DetailsAreaSmallTag
                  title={`${
                    isCollection ? 'Models are' : 'Model is'
                  } prepared for 3D printing`}
                  active
                  place="right"
                  tooltip={
                    `The designer indicates ${
                      isCollection ? 'that models are' : 'this model is'
                    } ` +
                    'prepared for physical 3D printing and includes printable file formats.'
                  }
                />
              ) : (
                <DetailsAreaSmallTag
                  title={`${
                    isCollection ? 'Models are' : 'Model is'
                  } not 3D printable`}
                  place="right"
                  tooltip={
                    `The designer indicates ${
                      isCollection ? 'that models are' : 'this model is'
                    } ` +
                    'intended for digital use only (rendering, animation, or AR/VR) ' +
                    'and not for 3D printing.'
                  }
                />
              )}
            </div>
          </div>

          {shouldShowGeometrySection && (
            <div>
              <span className="details-area__section-subtitle">Geometry</span>

              {typeof geometryType === 'string' && geometryType !== '' && (
                <div className="details-area__section-row details-area__section-row--geometry">
                  <DetailsAreaSmallTag
                    title={geometryType}
                    active
                    tooltip={geometryTypeTooltip}
                    autoPosition
                    containerSelector=".details-area__section-content"
                  />
                </div>
              )}

              {geometryItems.length > 0 && (
                <div className="details-area__section-row details-area__section-row--geometry">
                  {geometryItems.map((item, index) => {
                    const tag = (
                      <span className="details-area__small-tag">
                        {item.content}
                      </span>
                    )

                    return (
                      <React.Fragment key={item.key}>
                        {item.tooltip ? (
                          <SmartTooltip
                            content={item.tooltip}
                            id={item.key}
                            delayHide={100}
                            autoPosition
                            containerSelector=".details-area__section-content"
                            tooltipClassName="details-area__tooltip-content"
                          >
                            {tag}
                          </SmartTooltip>
                        ) : (
                          tag
                        )}
                        {index < geometryItems.length - 1 && ' / '}
                      </React.Fragment>
                    )
                  })}
                </div>
              )}
            </div>
          )}

          {is3dPrintModel && isPreSupported && (
            <div className="details-area__section">
              <span className="details-area__section-subtitle">
                Pre-supported or supports are not needed
              </span>
              <div className="details-area__section-info">
                <DetailsAreaSmallTag title="Yes" active />
              </div>
            </div>
          )}

          {is3dPrintModel && isSplitIntoMultipleParts && (
            <>
              <div className="details-area__section">
                <span className="details-area__section-subtitle">
                  Split into parts
                </span>
                <div className="details-area__section-info">
                  {multiplePartsSummary.instructions ? (
                    <Button
                      actionType="button"
                      type="ghost"
                      className="details-area__link-button"
                      onClick={() => setAssemblyInstructionsModalIsOpen(true)}
                      onMouseEnter={handleAssemblyInstructionsButtonMouseEnter}
                      onTouchStart={handleAssemblyInstructionsButtonTouchStart}
                    >
                      {splitIntoPartsLabel}
                    </Button>
                  ) : (
                    <span>{splitIntoPartsLabel}</span>
                  )}
                </div>
              </div>
              {multiplePartsSummary.dimensions && (
                <div className="details-area__section">
                  <span className="details-area__section-subtitle">
                    Dimensions (L x W x H)
                  </span>
                  <div className="details-area__section-info">
                    <span>{multiplePartsSummary.dimensions}</span>
                  </div>
                </div>
              )}
            </>
          )}

          {is3dPrintModel && units && (
            <div className="details-area__section">
              <span className="details-area__section-subtitle">Units</span>
              <div className="details-area__section-info">
                <span>{getUnitLabel(units)}</span>
              </div>
            </div>
          )}

          {!is3dPrintModel && (
            <div className="details-area__section">
              <span className="details-area__section-subtitle">
                Unwrapped UVs
              </span>
              <div className="details-area__section-info">
                <span>{formatUnwrappedUvValue(unwrappedUvs)}</span>
              </div>
            </div>
          )}

          <div className="details-area__section">
            <span className="details-area__section-subtitle">Publish date</span>
            <div className="details-area__section-info">
              <span>{publishDate}</span>
            </div>
          </div>

          <div className="details-area__section">
            <span className="details-area__section-subtitle">
              {isCollection ? 'Pack ID' : 'Model ID'}
            </span>
            <div className="details-area__section-info">
              <span>#{id}</span>
            </div>
          </div>
        </div>

        <Button
          type="secondary"
          size="tiny"
          variant="with-icon"
          customClass="details-area__report-button"
          trailIcon={<Icon svgComponent={Block} size="small" />}
          onClick={handleReportClick}
          onMouseEnter={handleReportButtonMouseEnter}
          onTouchStart={handleReportButtonTouchStart}
          disabled={isPreview}
          aria-label="Report"
        >
          Report
        </Button>
      </div>
      {reportModalIsOpen && isReportModalLoaded && ReportModal && (
        <ReportModal closeModal={handleCloseReportModal} itemId={id} />
      )}
      {assemblyInstructionsModalIsOpen &&
        multiplePartsSummary.instructions &&
        isAssemblyInstructionsModalLoaded &&
        AssemblyInstructionsModal && (
          <AssemblyInstructionsModal
            instructions={multiplePartsSummary.instructions}
            onClose={() => setAssemblyInstructionsModalIsOpen(false)}
          />
        )}
    </>
  )
}

ProvidedByDesignerSection.propTypes = {
  publishDate: string,
  id: number.isRequired,
  polygons: number,
  vertices: number,
  triangles: number,
  geometryType: oneOfType([string, object]),
  units: string,
  isCollection: bool,
  unwrappedUvs: oneOf(['yes', 'no', 'unknown', 'mixed', 'non-overlapping']),
  details: oneOfType([
    shape({
      animated: bool,
      rigged: bool,
      gameReady: bool,
      pbr: bool,
      textures: bool,
      materials: bool,
      uvMapping: bool,
      pluginsUsed: bool,
    }),
    arrayOf(
      shape({
        title: string,
        value: oneOfType([string, number, bool]),
      })
    ),
  ]),
  itemsPerRow: number,
  isPreSupported: bool,
  isSplitIntoMultipleParts: bool,
  multiplePartsDetails: shape({
    numberOfParts: string,
    unit: string,
    width: string,
    depth: string,
    height: string,
    assemblyInstructions: string,
  }),
  tagForLinking: string,
}

export default ProvidedByDesignerSection
